import { X } from 'lucide-react';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import Image from 'next/image';
import Modal from '@/components/v2/common/Modal';
import reebeloCareIcon from '@/public/icons/product/reebeloCareIcon.svg';
import reebeloCareBanner from '@/public/images/banner/reebelocare-banner.png';
import settings from '@/settings/index';
import {
  benefitsList,
  warrantySlideOverTitles,
} from '../../product/sections/main/right/variants/reebelocare/constants';
import ProductWarrantyDisclaimer from '../../product/sections/main/right/variants/reebelocare/ProductWarrantyDisclaimer';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const BulletList = ({ text, icon }: { text: string; icon: ReactNode }) => (
  <div className="flex gap-3 text-sm">
    {icon}
    <div
      className="flex items-center lg:items-start"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: text }}
    />
  </div>
);

const Header = ({
  setShowModal,
}: {
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => (
  <div className="sticky top-0 z-[70] flex items-center justify-between bg-white p-4 text-sm shadow">
    <div>
      <h1 className="w-full text-base font-extrabold leading-5">
        {t`About ReebeloCare`}
      </h1>
    </div>
    <button
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white  transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
      onClick={() => setShowModal(false)}
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);

const Content = () => {
  // Title
  const title =
    warrantySlideOverTitles[settings.store] ?? warrantySlideOverTitles.default;
  // Benefits
  const benefits = benefitsList[settings.store] ?? benefitsList.default;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-[150px] items-center justify-between overflow-y-hidden bg-[#ECFCF5]">
        <div className="mt-2">
          <Image
            width={125}
            height={125}
            alt={t`close`}
            src={reebeloCareIcon}
          />
        </div>
        <div className="h-full">
          <Image width={255} height={260} alt={t`rc`} src={reebeloCareBanner} />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-between gap-4 p-4">
        <div>
          <h4 className="text-center font-bold">{title}</h4>
          <hr className="my-4" />
          <div className="flex flex-col justify-center gap-5 lg:gap-6">
            {benefits.map((item) => (
              <BulletList
                key={item.content}
                text={item.content}
                icon={item.icon}
              />
            ))}
          </div>
        </div>
        <ProductWarrantyDisclaimer />
      </div>
    </div>
  );
};

const NzContent = () => (
  <div className="flex flex-col text-sm">
    <div className="overflow-y-auto pb-4">
      <div className=" flex h-[150px] items-center justify-between overflow-y-hidden bg-[#ECFCF5]">
        <div className="mt-2">
          <Image
            width={125}
            height={125}
            alt={t`close`}
            src={reebeloCareIcon}
          />
        </div>
        <div className="h-full">
          <Image width={255} height={260} alt={t`rc`} src={reebeloCareBanner} />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-between gap-4 p-4">
        <div>
          <h4 className="text-base font-bold">
            {t`Plan Summary, Service Fees & Limits`}
          </h4>
          <p className="text-sm">
            {t`ReebeloCare is an optional 24-month, any-reason service plan that gives you access to device repair services or a replacement if repair isn’t suitable.`}
          </p>
          <hr className="my-3" />
          <div className="flex flex-col justify-center gap-8">
            <div>
              <h3 className="font-bold">{t`Repair Service`}</h3>
              <p>
                {t`Request a repair for any reason, including cracked screen, liquid spill, or battery issue. Our repair service covers a full diagnostic and component replacement or refurbishment (screen, battery, and more).`}
              </p>
              <ul className="ml-4 mt-2 list-disc space-y-1">
                <li>
                  {t`A service fee of 15% of your original device purchase price applies per repair request.`}
                </li>
                <li>
                  {t`The total combined value of all services is capped at your original device purchase price.`}
                </li>
                <li>
                  {t`Walk-in repair services will be offered based on availability if you're in Auckland, Wellington, and Christchurch.`}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold">{t`Replacement Service`}</h3>
              <p>
                {t`Device replacement is available only where a repair is not suitable.`}
              </p>
              <ul className="ml-4 mt-2 list-disc space-y-1">
                <li>
                  {t`A service fee of 25% of your original device purchase price applies.`}
                </li>
                <li>
                  {t`Replacement will be the same model or closest equivalent; colour may vary.`}
                </li>
                <li>
                  {t`If a like-for-like replacement is unavailable, or we can only access a replacement at a cost greater than your remaining plan value, you'll receive a Prepaid Mastercard or Reebelo Gift Card equal to your remaining plan value (original purchase price less any services already used).`}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold">{t`Plan Terms`}</h3>
              <p>
                {t`Your plan runs for up to 24 months from purchase, with service value up to your original device purchase price. Your plan expires after 24 months, or when your plan value is fully used, or when a replacement device or Prepaid Mastercard/Reebelo Gift Card has been provided, whichever comes first.`}
              </p>
            </div>
          </div>
        </div>
        <ProductWarrantyDisclaimer />
      </div>
    </div>
  </div>
);

const ReebeloCareModal = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const content = settings.store === 'reebelo-nz' ? <NzContent /> : <Content />;

  return (
    <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
      <Header setShowModal={setShowModal} />
      {content}
    </Modal>
  );
};

export default ReebeloCareModal;
